import type { FC, ReactNode } from 'react'
import type {
    ContentBlock,
    MediaSlot as MediaSlotRow,
    Testimonial
} from '@/types/content'

import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button-link'
import { Container } from '@/components/generals/container'
import { MediaSlot } from '@/components/generals/media-slot'
import { SectionIntro } from '@/components/generals/section-intro'
import { TestimonialCard, TestimonialPlaceholder } from '@/components/sections/testimonial-card'
import { cms, hasValue } from '@/lib/cms'
import { reveal } from '@/lib/reveal'

interface ResultsSectionProps {
    block?: ContentBlock
    testimonials: Array<Testimonial>
    video?: MediaSlotRow
    result?: MediaSlotRow
    note?: ContentBlock
    // Headline level: h2 inside a page, h1 when the section opens the results page
    as?: 'h1' | 'h2'
    showLink?: boolean
    showAll?: boolean
    className?: string
}

// Section 07: featured video testimonial, featured result and written experiences.
// Until real, released content exists, every spot is a clearly marked placeholder.
export const ResultsSection: FC<ResultsSectionProps> = ({
    block,
    testimonials,
    video,
    result,
    note,
    as = 'h2',
    showLink = true,
    showAll = false,
    className
}): ReactNode => {
    const listed = showAll ? testimonials : testimonials.slice(0, 2)
    const placeholders = Math.max(0, 2 - listed.length)

    return (
        <section id='ergebnisse' aria-labelledby='ergebnisse-titel' className={cn('relative overflow-hidden bg-ink py-24 text-bone sm:py-32 lg:py-40', className)}>
            <div aria-hidden='true' className='grain-layer' />

            <Container className='relative'>
                <div className='flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionIntro
                        block={block}
                        as={as}
                        headingId='ergebnisse-titel'
                        size={as === 'h1' ? 'xl' : 'lg'}
                        tone='dark'
                        motion={as === 'h1' ? 'entrance' : 'reveal'}
                        className='lg:max-w-3xl'
                    />

                    {showLink && block && hasValue(block.button_text) && hasValue(block.button_link) && (
                        <div {...reveal(120)}>
                            <ButtonLink
                                href={block.button_link}
                                label={block.button_text}
                                labelCms={cms('inhalte', block.id, 'button_text')}
                                variant='secondary'
                                tone='dark'
                            />
                        </div>
                    )}
                </div>

                <div className='mt-16 grid gap-10 md:grid-cols-2 lg:mt-24 lg:grid-cols-12 lg:gap-8'>
                    <div className='lg:col-span-4' {...reveal()}>
                        <MediaSlot
                            slot={video}
                            slotKey='TESTIMONIAL_VIDEO_01'
                            videoMode='player'
                            className='mx-auto aspect-[9/16] w-full max-w-[24rem] md:max-w-none'
                            figureClassName='h-full'
                            tone='dark'
                            showCaption
                        />
                    </div>

                    <div className='lg:col-span-4' {...reveal(100)}>
                        <MediaSlot
                            slot={result}
                            slotKey='CUSTOMER_RESULT_01'
                            className='aspect-[4/5] w-full'
                            tone='dark'
                            showCaption
                        />
                    </div>

                    <div className='flex flex-col gap-8 md:col-span-2 lg:col-span-4' {...reveal(200)}>
                        {listed.map(testimonial => (
                            <TestimonialCard key={testimonial.id} testimonial={testimonial} showMedia={showAll} />
                        ))}

                        {Array.from({ length: placeholders }).map((_, index) => (
                            <TestimonialPlaceholder key={`platzhalter-${index}`} />
                        ))}
                    </div>
                </div>

                {showAll && testimonials.length > 2 && (
                    <ul className='mt-20 grid gap-x-8 gap-y-14 md:grid-cols-2 lg:grid-cols-3'>
                        {testimonials.slice(2).map((testimonial, index) => (
                            <li key={testimonial.id} className='flex' {...reveal((index % 3) * 90)}>
                                <TestimonialCard testimonial={testimonial} showMedia className='w-full' />
                            </li>
                        ))}
                    </ul>
                )}

                {note && (hasValue(note.ueberschrift) || hasValue(note.text)) && (
                    <aside className='mt-20 flex flex-col gap-3 border-t border-ink-line pt-8 sm:flex-row sm:gap-10' {...reveal()}>
                        {hasValue(note.ueberschrift) && (
                            <p data-mdlfy-cms={cms('inhalte', note.id, 'ueberschrift')} className='shrink-0 font-semibold text-bone sm:w-72'>
                                {note.ueberschrift}
                            </p>
                        )}

                        {hasValue(note.text) && (
                            <p data-mdlfy-cms={cms('inhalte', note.id, 'text')} className='max-w-2xl text-sm leading-relaxed text-ash'>
                                {note.text}
                            </p>
                        )}
                    </aside>
                )}
            </Container>
        </section>
    )
}
